import {LitElement, PropertyValues, html, nothing} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";

import {citationEditorStyles} from "./CitationEditor.styles.js";
import {
	citationPanelData,
	generateCitation,
	getFromPanel_IndividualFieldsCslData,
	getFromPanel_SearchValue
} from "./panel_utils.js";
import {RichTextEditor, EXTENSIONS} from "../../common/controls-inputs/rich-text-editor/RichTextEditor.js";
import {Editor} from "@tiptap/core";
import {CitationPanel, STATES} from "./CitationPanel.js";
import Citation from "../../../main/citation.js";
import Data from "../../../main/data/index.js";

import "../../common/controls-inputs/IconButton.js";

const PLACEHOLDERS = {
	COULD_NOT_GENERATE: "We couldn't generate this citation. You can type the citation in here."
};

@customElement("pn-citation-editor")
export class CitationEditor extends LitElement {
	@property() citation:                citationPanelData;
	@property() editable:                boolean;
	@property() isAutoCitationsDisabled: boolean;
	@property() isRefreshAllowed:        boolean;
	@property() state:                   string;

	@state() _isLoading = false;
	@state() _isRenderModified: boolean;
	@state() _showWarning = false;
	@state() _warningMessage = "The generated citation has been edited.";

	@query("pn-rte") _rte: RichTextEditor;

	static styles = [citationEditorStyles];

	willUpdate(changedProperties: PropertyValues<this>) {
		if (changedProperties.has("citation")) {
			const isModified = this.citation.render_modified ?? false;
			this.updateModified(isModified);
		}
	}

	updateModified(isModified: boolean) {
		if (isModified) {
			this._isRenderModified = true;

			if (!this.isAutoCitationsDisabled)
				this._showWarning = true;
		} else
			this._showWarning = false;
	}

	onClickRefresh = async () => {
		this._isLoading = true;

		// Reset isRenderModified
		this._isRenderModified = false;
		this._showWarning = false;

		let errorMessage = "";

		try {
			// Get new citation object to generate rendered_citation from
			let citation = {};

			const citationInfo = (this.getRootNode() as ShadowRoot).host;
			const citationPanel = (citationInfo.getRootNode() as ShadowRoot).host as CitationPanel;

			if (this.state === STATES.STANDARD) {
				errorMessage = "Something went wrong while retrieving citation.";

				const dataFromPanel = getFromPanel_IndividualFieldsCslData(citationPanel);
				citation = await generateCitation(citationPanel, dataFromPanel);
			} else if (this.state === STATES.SEARCH) {
				const searchValue = getFromPanel_SearchValue(citationPanel);
				errorMessage = `Something went wrong when searching for "${searchValue}"`;

				const citationData = await Citation.search(searchValue);
				citation = Object.assign(this.citation, {...citationData});
			}

			const rendered_citation = await Data.app.backend.getRenderedCitation({citation});
			this._rte.content = rendered_citation;
		} catch (e) {
			this._warningMessage = errorMessage;
			this._showWarning = true;
		}

		// Reset refresh button
		this._isLoading = false;
	};

	onCitationEditorUpdate = ({editor}: {editor: Editor}) => {
		const isModified = editor.getHTML() !== this.citation.rendered_citation;
		this.updateModified(isModified);
	};

	render() {
		const placeholder = (this.citation?.auto_citation_error !== "")
			? PLACEHOLDERS.COULD_NOT_GENERATE
			: "";

		return html`
			<div id="citation-field-label-wrapper">
				<label class="is-bold" id="citation-field-label">Citation</label>

				<span
					class="${classMap({
						hidden: !this._showWarning
					})}"
					id="citation-editor-warning"
					role="status"
				>${this._warningMessage}</span>

				${this.isAutoCitationsDisabled
					? html`<span
						id="citation-editor-notification"
						role="status"
					>
						Automatic citations disabled for this project.
					</span>`
					: nothing
				}

				${this.isRefreshAllowed
					? html`
						<pn-icon-button
							class=${this._isLoading ? "loading" : nothing}
							.className=${this._isLoading ? "loading" : nothing}
							.disabled="${this.isAutoCitationsDisabled || this._isLoading}"
							icon="refresh-circle"
							id="btn-citation-refresh"
							.isMask="${true}"
							.onClick="${this.onClickRefresh}"
							.tooltipText="${"Refresh"}"
						></pn-icon-button>
					`
					: nothing
				}
			</div>

			<pn-rte
				class="${classMap({
					"is-render-modified": this._isRenderModified
				})}"
				id="citation-editor"
				.content="${this.citation.rendered_citation}"
				.editable="${this.editable}"
				.enabledExtensions="${[
					EXTENSIONS.BOLD,
					EXTENSIONS.ITALIC,
					EXTENSIONS.LINK,
					EXTENSIONS.SMALLCAPS,
					EXTENSIONS.SUBSCRIPT,
					EXTENSIONS.SUPERSCRIPT,
					EXTENSIONS.UNDERLINE
				]}"
				.disableEmojiBtn="${true}"
				.isLoading=${this._isLoading}
				.limit="${1024}"
				.onUpdate="${this.onCitationEditorUpdate}"
				.placeholder="${placeholder}"
				.preserveWhitespaceFull="${false}"
			></pn-rte>
		`;
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-citation-editor": CitationEditor;
	}
}
